import { supabase } from '@/lib/supabase'
import type { 
  Review, 
  GMBLocation, 
  Service, 
  ReviewResponse,
  ReviewFilters, 
  ReviewStats, 
  ReviewTableData,
  ReviewsQuery 
} from '@/types/reviews'

type ReviewSource = 'manual' | 'gmb' | 'jobber' | 'website' | 'other'

/**
 * Convert string star rating to number
 */
function convertStarRatingToNumber(rating: string): number {
  const ratingMap: Record<string, number> = {
    'ONE': 1,
    'TWO': 2,
    'THREE': 3,
    'FOUR': 4,
    'FIVE': 5
  }
  return ratingMap[rating] || 0
}

/**
 * Fetch reviews with filtering, sorting, and pagination
 */
export async function fetchReviews(query: ReviewsQuery = {}): Promise<ReviewTableData> {
  const { filters = {}, sort, page = 1, page_size = 20 } = query
  
  // Check if we're filtering by source, default to gmb_reviews for GMB integration
  const isGMBSource = filters.source === 'gmb' || (!filters.source && filters.location_id)
  const tableName = isGMBSource ? 'gmb_reviews' : 'reviews'
  
  console.log('fetchReviews:', { filters, isGMBSource, tableName })
  
  let supabaseQuery = supabase
    .from(tableName)
    .select('*', { count: 'exact' })
  
  // Apply filters with table-specific column names
  if (filters.search) {
    if (isGMBSource) {
      // Skip search for now in GMB reviews to avoid column errors
      // supabaseQuery = supabaseQuery.or(`reviewer_name.ilike.%${filters.search}%,comment.ilike.%${filters.search}%`)
    } else {
      supabaseQuery = supabaseQuery.or(`reviewer_name.ilike.%${filters.search}%,reviewer_comment.ilike.%${filters.search}%`)
    }
  }
  
  if (filters.location_id) {
    const locationColumn = isGMBSource ? 'gmb_location_id' : 'location_id'
    supabaseQuery = supabaseQuery.eq(locationColumn, filters.location_id)
  }
  
  if (filters.service_id && !isGMBSource) {
    supabaseQuery = supabaseQuery.eq('service_id', filters.service_id)
  }
  
  if (filters.rating_min) {
    supabaseQuery = supabaseQuery.gte('star_rating', filters.rating_min)
  }
  
  if (filters.rating_max) {
    supabaseQuery = supabaseQuery.lte('star_rating', filters.rating_max)
  }
  
  if (filters.date_from) {
    const dateColumn = isGMBSource ? 'review_created_at' : 'reviewer_create_time'
    supabaseQuery = supabaseQuery.gte(dateColumn, filters.date_from)
  }
  
  if (filters.date_to) {
    const dateColumn = isGMBSource ? 'review_created_at' : 'reviewer_create_time'
    supabaseQuery = supabaseQuery.lte(dateColumn, filters.date_to)
  }
  
  if (filters.has_response !== undefined) {
    if (filters.has_response) {
      supabaseQuery = supabaseQuery.not('reply_comment', 'is', null)
    } else {
      supabaseQuery = supabaseQuery.is('reply_comment', null)
    }
  }
  
  if (filters.response_needed) {
    if (isGMBSource) {
      supabaseQuery = supabaseQuery.eq('needs_response', true)
    } else {
      supabaseQuery = supabaseQuery.is('reply_comment', null)
    }
  }
  
  // Apply sorting
  if (sort) {
    // Map field names for GMB reviews if needed
    let sortField = sort.field
    if (isGMBSource && sort.field === 'reviewer_create_time') {
      sortField = 'review_created_at'
    }
    supabaseQuery = supabaseQuery.order(sortField, { ascending: sort.direction === 'asc' })
  } else {
    // Default sort by most recent
    const dateColumn = isGMBSource ? 'review_created_at' : 'reviewer_create_time'
    supabaseQuery = supabaseQuery.order(dateColumn, { ascending: false })
  }
  
  // Apply pagination
  const offset = (page - 1) * page_size
  supabaseQuery = supabaseQuery.range(offset, offset + page_size - 1)
  
  const { data, count, error } = await supabaseQuery
  
  if (error) {
    console.error('Error fetching reviews:', error)
    // Error fetching reviews - handled by throwing
    throw new Error(`Failed to fetch reviews: ${error.message}`)
  }
  
  console.log('Reviews data:', { data, count })

  // Transform the data to match the expected format
  const transformedData = data?.map(review => {
    if (isGMBSource) {
      // Transform GMB review to match expected format
      return {
        id: review.id,
        review_id: review.gmb_review_id,
        reviewer_name: review.reviewer_name,
        reviewer_comment: review.comment,
        star_rating: review.star_rating,
        reviewer_create_time: review.review_created_at,
        reviewer_update_time: review.review_updated_at,
        reply_comment: review.reply_comment,
        reply_update_time: review.reply_updated_at,
        location_id: review.gmb_location_id,
        source: 'gmb' as ReviewSource,
        needs_response: review.needs_response,
        is_flagged: review.is_flagged,
        flag_reason: review.flag_reason,
        // Additional GMB-specific fields
        reviewer_photo_url: review.reviewer_photo_url,
        is_anonymous: review.is_anonymous,
        synced_at: review.synced_at
      }
    }
    return {
      ...review,
      // Convert string star rating to number for UI consistency
      star_rating_number: convertStarRatingToNumber(review.star_rating),
      // Add alias for backward compatibility
      google_create_time: review.reviewer_create_time,
      google_update_time: review.reviewer_update_time
    }
  }) || []
  
  return {
    reviews: transformedData || [],
    total_count: count || 0,
    page,
    page_size,
    total_pages: Math.ceil((count || 0) / page_size)
  }
}

/**
 * Fetch review statistics for dashboard widgets
 */
export async function fetchReviewStats(): Promise<ReviewStats> {
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  
  const sixtyDaysAgo = new Date()
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
  
  try {
    // Total reviews and current month
    const { data: currentPeriod, error: currentError } = await supabase
      .from('reviews')
      .select('star_rating, reply_comment')
      .gte('reviewer_create_time', thirtyDaysAgo.toISOString())
    
    if (currentError) throw currentError
    
    // Previous month for comparison
    const { data: previousPeriod, error: previousError } = await supabase
      .from('reviews')
      .select('star_rating, reply_comment')
      .gte('reviewer_create_time', sixtyDaysAgo.toISOString())
      .lt('reviewer_create_time', thirtyDaysAgo.toISOString())
    
    if (previousError) throw previousError
    
    // All time stats
    const { data: allReviews, error: allError } = await supabase
      .from('reviews')
      .select('star_rating, reply_comment')
    
    if (allError) throw allError
    
    // Calculate stats
    const totalReviews = allReviews?.length || 0
    const reviewsThisMonth = currentPeriod?.length || 0
    const reviewsLastMonth = previousPeriod?.length || 0
    
    const averageRating = allReviews?.length 
      ? allReviews.reduce((sum, review) => sum + convertStarRatingToNumber(review.star_rating), 0) / allReviews.length 
      : 0
    
    const currentAvgRating = currentPeriod?.length
      ? currentPeriod.reduce((sum, review) => sum + convertStarRatingToNumber(review.star_rating), 0) / currentPeriod.length
      : 0
    
    const previousAvgRating = previousPeriod?.length
      ? previousPeriod.reduce((sum, review) => sum + convertStarRatingToNumber(review.star_rating), 0) / previousPeriod.length
      : 0
    
    const ratingChange = currentAvgRating - previousAvgRating
    
    const totalResponded = allReviews?.filter(r => r.reply_comment).length || 0
    const responseRate = totalReviews > 0 ? (totalResponded / totalReviews) * 100 : 0
    
    const currentResponded = currentPeriod?.filter(r => r.reply_comment).length || 0
    const currentResponseRate = currentPeriod?.length 
      ? (currentResponded / currentPeriod.length) * 100 
      : 0
    
    const previousResponded = previousPeriod?.filter(r => r.reply_comment).length || 0
    const previousResponseRate = previousPeriod?.length 
      ? (previousResponded / previousPeriod.length) * 100 
      : 0
    
    const responseRateChange = currentResponseRate - previousResponseRate
    
    const pendingResponses = allReviews?.filter(r => !r.reply_comment).length || 0
    
    return {
      total_reviews: totalReviews,
      average_rating: Math.round(averageRating * 10) / 10,
      response_rate: Math.round(responseRate),
      pending_responses: pendingResponses,
      reviews_this_month: reviewsThisMonth,
      rating_change: Math.round(ratingChange * 10) / 10,
      response_rate_change: Math.round(responseRateChange)
    }
  } catch (error) {
    console.error('Error fetching review stats:', error)
    throw new Error('Failed to fetch review statistics')
  }
}

/**
 * Fetch all GMB locations for filtering
 */
export async function fetchGMBLocations(): Promise<GMBLocation[]> {
  const { data, error } = await supabase
    .from('gmb_locations')
    .select('*')
    .eq('status', 'active')
    .order('name')
  
  if (error) {
    console.error('Error fetching GMB locations:', error)
    throw new Error(`Failed to fetch locations: ${error.message}`)
  }
  
  return data || []
}

/**
 * Fetch all services for filtering and auto-tagging
 */
export async function fetchServices(): Promise<Service[]> {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('name')
  
  if (error) {
    console.error('Error fetching services:', error)
    throw new Error(`Failed to fetch services: ${error.message}`)
  }
  
  return data || []
}

/**
 * Fetch a single review by ID
 */
export async function fetchReviewById(id: string): Promise<Review | null> {
  const { data, error } = await supabase
    .from('reviews')
    .select(`
      *,
      gmb_locations!inner(name, place_id),
      services(name, category)
    `)
    .eq('id', id)
    .single()
  
  if (error) {
    if (error.code === 'PGRST116') {
      return null // Review not found
    }
    console.error('Error fetching review:', error)
    throw new Error(`Failed to fetch review: ${error.message}`)
  }
  
  return data
}

/**
 * Create a response to a review
 */
export async function createReviewResponse(
  reviewId: string, 
  responseText: string, 
  publishToGoogle: boolean = false
): Promise<ReviewResponse> {
  const response = {
    review_id: reviewId,
    response_text: responseText,
    published_to_google: publishToGoogle,
    published_at: publishToGoogle ? new Date().toISOString() : null,
    created_by: 'admin', // TODO: Get from auth context
  }
  
  const { data, error } = await supabase
    .from('review_responses')
    .insert(response)
    .select()
    .single()
  
  if (error) {
    console.error('Error creating review response:', error)
    throw new Error(`Failed to create response: ${error.message}`)
  }
  
  // Also update the review with the response
  await supabase
    .from('reviews')
    .update({
      reply_comment: responseText,
      reply_update_time: new Date().toISOString()
    })
    .eq('id', reviewId)
  
  return data
}

/**
 * Update an existing review response
 */
export async function updateReviewResponse(
  responseId: string,
  responseText: string,
  publishToGoogle: boolean = false
): Promise<ReviewResponse> {
  const updates = {
    response_text: responseText,
    published_to_google: publishToGoogle,
    published_at: publishToGoogle ? new Date().toISOString() : null,
    updated_at: new Date().toISOString()
  }
  
  const { data, error } = await supabase
    .from('review_responses')
    .update(updates)
    .eq('id', responseId)
    .select()
    .single()
  
  if (error) {
    console.error('Error updating review response:', error)
    throw new Error(`Failed to update response: ${error.message}`)
  }
  
  return data
}

/**
 * Auto-tag reviews using service keywords
 */
export async function autoTagReviews(reviewIds?: string[]): Promise<number> {
  try {
    // Fetch services for keyword matching
    const services = await fetchServices()
    
    // Build query for reviews to process
    let query = supabase
      .from('reviews')
      .select('id, reviewer_comment, auto_tagged')
    
    if (reviewIds && reviewIds.length > 0) {
      query = query.in('id', reviewIds)
    } else {
      // Only process untagged reviews
      query = query.eq('auto_tagged', false)
    }
    
    const { data: reviews, error } = await query
    
    if (error) throw error
    
    let taggedCount = 0
    
    for (const review of reviews || []) {
      const comment = review.reviewer_comment.toLowerCase()
      let bestMatch: { serviceId: string; confidence: number } | null = null
      
      for (const service of services) {
        const keywords = service.keywords || []
        let matches = 0
        
        for (const keyword of keywords) {
          if (comment.includes(keyword.toLowerCase())) {
            matches++
          }
        }
        
        if (matches > 0) {
          const confidence = matches / keywords.length
          if (!bestMatch || confidence > bestMatch.confidence) {
            bestMatch = { serviceId: service.id, confidence }
          }
        }
      }
      
      if (bestMatch && bestMatch.confidence >= 0.3) { // 30% confidence threshold
        await supabase
          .from('reviews')
          .update({
            service_id: bestMatch.serviceId,
            tag_confidence: bestMatch.confidence,
            auto_tagged: true
          })
          .eq('id', review.id)
        
        taggedCount++
      }
    }
    
    return taggedCount
  } catch (error) {
    console.error('Error auto-tagging reviews:', error)
    throw new Error('Failed to auto-tag reviews')
  }
}